import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Power, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BulkImportButton } from "../components/common/BulkImportButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";

export default function GeoMasterPanel({ kind, label }) {
  const { has } = useAuth();
  const canManage = has("MASTERS.MANAGE");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [search, setSearch] = useState("");
  const [projects, setProjects] = useState([]);
  const projName = (pid) => { const p = projects.find((x) => x.id === pid); return p ? (p.project_code ? `${p.project_code} — ${p.project_name}` : p.project_name) : pid; };

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/geo-masters/${kind}`, { params: { include_inactive: true } }); setRows(data.items || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, [kind]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/projects", { params: { page_size: 200 } }).then((r) => setProjects(r.data.items || [])).catch(() => {}); }, []);

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    try {
      if (form.id) await api.put(`/geo-masters/${kind}/${form.id}`, { name: form.name, project_ids: form.project_ids || [] });
      else await api.post(`/geo-masters/${kind}`, { name: form.name, project_ids: form.project_ids || [] });
      toast.success("Saved"); setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (r) => {
    try { await api.patch(`/geo-masters/${kind}/${r.id}/status`, null, { params: { is_active: !r.is_active } }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q ? rows.filter((r) => (r.name || "").toLowerCase().includes(q)) : rows;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-slate-500">{label} master — appears as a searchable list in the Data Entry consumer override</span>
        <div className="flex flex-wrap items-center gap-2">
          <Input data-testid={`${kind}-search`} value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${label.toLowerCase()}...`} className="h-8 w-56" />
          {canManage && <BulkImportButton testid={`${kind}-import`} label="Bulk Upload"
            templateUrl={`/geo-masters/${kind}/template`} importUrl={`/geo-masters/${kind}/import`}
            templateName={`${kind}_master_template.xlsx`} onDone={load} />}
          {canManage && <Button data-testid={`${kind}-add`} size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setForm({ name: "", project_ids: [] })}><Plus className="mr-1.5 h-4 w-4" /> New {label}</Button>}
        </div>
      </div>
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left">
          <thead><tr className="border-b border-slate-200 bg-slate-100/80">
            <th className="px-4 py-2.5 text-xs font-semibold uppercase text-slate-500">Name</th>
            <th className="px-4 py-2.5 text-xs font-semibold uppercase text-slate-500">Projects</th>
            <th className="px-4 py-2.5 text-xs font-semibold uppercase text-slate-500">Status</th>
            <th className="px-4 py-2.5" />
          </tr></thead>
          <tbody data-testid={`${kind}-rows`}>
            {loading ? <tr><td colSpan={4} className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>
              : filtered.length === 0 ? <tr><td colSpan={4} className="py-8 text-center text-sm text-slate-400" data-testid={`${kind}-empty`}>{rows.length ? `No ${label.toLowerCase()} match "${search}".` : `No ${label.toLowerCase()} yet — add one or bulk upload`}</td></tr>
                : filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`${kind}-row-${r.id}`}>
                    <td className="px-4 py-2.5 text-sm text-slate-800">{r.name}</td>
                    <td className="px-4 py-2.5">{(r.project_ids || []).length
                      ? <div className="flex flex-wrap gap-1">{r.project_ids.map((pid) => <Badge key={pid} variant="outline" className="text-xs font-medium text-slate-600" data-testid={`${kind}-proj-badge-${r.id}-${pid}`}>{projName(pid)}</Badge>)}</div>
                      : <span className="text-xs text-slate-400">All projects</span>}</td>
                    <td className="px-4 py-2.5"><Badge variant={r.is_active ? "default" : "outline"} className={r.is_active ? "bg-emerald-600" : "text-slate-400"}>{r.is_active ? "Active" : "Inactive"}</Badge></td>
                    <td className="px-4 py-2.5 text-right">{canManage && (
                      <div className="flex justify-end gap-1">
                        <Button data-testid={`${kind}-edit-${r.id}`} variant="ghost" size="sm" onClick={() => setForm({ id: r.id, name: r.name, project_ids: r.project_ids || [] })}><Pencil className="h-4 w-4" /></Button>
                        <Button data-testid={`${kind}-toggle-${r.id}`} variant="ghost" size="sm" onClick={() => toggle(r)}><Power className={`h-4 w-4 ${r.is_active ? "text-emerald-600" : "text-slate-400"}`} /></Button>
                      </div>
                    )}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}><DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">{form?.id ? `Edit ${label}` : `New ${label}`}</DialogTitle></DialogHeader>
        <div><Label className="text-xs">Name *</Label><Input data-testid={`${kind}-name-input`} value={form?.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
        <div className="mt-3">
          <Label className="text-xs">Tag to Projects <span className="font-normal text-slate-400">(none selected = visible in all projects)</span></Label>
          <div className="mt-1 max-h-36 space-y-1.5 overflow-y-auto rounded-md border border-slate-200 p-2" data-testid={`${kind}-projects`}>
            {projects.map((p) => (
              <label key={p.id} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700" data-testid={`${kind}-proj-${p.id}`}>
                <Checkbox checked={(form?.project_ids || []).includes(p.id)}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, project_ids: v ? [...(f.project_ids || []), p.id] : (f.project_ids || []).filter((x) => x !== p.id) }))} />
                <span>{p.project_code ? `${p.project_code} — ` : ""}{p.project_name}</span>
              </label>
            ))}
            {!projects.length && <span className="text-xs text-slate-400">No projects available</span>}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Cancel</Button><Button data-testid={`${kind}-save`} onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
